import { z } from 'zod';
import { listQueryShape, pageOf } from './lists.js';
import { RuleOutcome } from './obligations.js';
/**
 * The first contact of a prospective client through the firm's portal (development paper §6.1,
 * step 1 "Ersterfassung", ADR-023): who it is, what it is about - and, since ADR-068, the first
 * facts the firm's target profile is compared with (preselection).
 */
/** An address of the firm's own website (HTTPS only), e.g. its legal notice. */
const FirmPage = z.url({ protocol: /^https$/ }).max(500);
/**
 * The firm's legal pages the portal links to (Art. 13 GDPR, § 5 DDG): the firm is the
 * controller, so its own notices apply. Without both, the portal takes no inquiry.
 */
export const FirmPortalLegal = z
    .object({
    imprintUrl: FirmPage.nullable(),
    privacyUrl: FirmPage.nullable(),
})
    .meta({ id: 'FirmPortalLegal' });
/** Whether the portal of this host takes inquiries: CHECK-POINT and both legal pages (ADR-023). */
export const PortalIntake = z.object({ open: z.boolean() }).meta({ id: 'PortalIntake' });
/**
 * How long the firm keeps inquiries that led to no mandate (decision of 2026-10-27, ADR-024):
 * months after they were closed - or received, if never answered. No duty to keep them applies,
 * only data minimisation; the firm, as the controller, may choose between 1 and 24 months.
 */
export const INQUIRY_RETENTION_DEFAULT_MONTHS = 6;
export const InquiryRetention = z
    .object({ months: z.int().min(1).max(24) })
    .meta({ id: 'InquiryRetention' });
export const ClientKind = z.enum(['private', 'business', 'association']).meta({ id: 'ClientKind' });
export const clientKindLabel = {
    private: 'Privatperson',
    business: 'Unternehmen',
    association: 'Verein oder Stiftung',
};
/** What a prospective client may ask the firm for (first facts, ADR-068). */
export const InquiryService = z
    .enum([
    'bookkeeping',
    'payroll',
    'annual_accounts',
    'business_tax_returns',
    'private_tax_returns',
    'tax_advice',
    'business_formation',
    'succession',
])
    .meta({ id: 'InquiryService' });
export const inquiryServiceLabel = {
    bookkeeping: 'Finanzbuchhaltung',
    payroll: 'Lohnbuchhaltung',
    annual_accounts: 'Jahresabschluss',
    business_tax_returns: 'Betriebliche Steuererklärungen',
    private_tax_returns: 'Einkommensteuererklärung',
    tax_advice: 'Steuerliche Beratung',
    business_formation: 'Existenzgründung',
    succession: 'Unternehmensnachfolge',
};
/** How many people an organisation employs, in bands (first facts, ADR-068). */
export const EmployeeBand = z
    .enum(['none', 'small', 'medium', 'large'])
    .meta({ id: 'EmployeeBand' });
export const employeeBandLabel = {
    none: 'Keine Mitarbeitenden',
    small: '1 bis 9',
    medium: '10 bis 49',
    large: '50 und mehr',
};
/** A German postal code, five digits. */
const PostalCode = z
    .string()
    .trim()
    .regex(/^\d{5}$/);
/** What a prospective client sends in step 1 - no more than the firm needs to answer. */
export const InquiryInput = z
    .object({
    kind: ClientKind,
    /** The person's or the organisation's name. */
    name: z.string().trim().min(2).max(200),
    email: z.email().max(254),
    phone: z
        .string()
        .trim()
        .regex(/^[+0-9 ()/-]{6,30}$/)
        .nullable(),
    /** What the inquiry is about, in the person's own words. */
    concern: z.string().trim().min(10).max(2000),
    /** What the person asks for (ADR-068); left out: not stated. */
    services: z.array(InquiryService).max(InquiryService.options.length).default([]),
    /** Where the person lives or the organisation sits; `null`: not stated. */
    postalCode: PostalCode.nullable().default(null),
    /** How many people an organisation employs; `null`: not stated (or a private person). */
    employees: EmployeeBand.nullable().default(null),
    /** The firm's privacy notice the person was shown and confirmed reading. */
    privacyNoticeUrl: FirmPage,
    /** Honeypot: a field people do not see; whatever fills it is not a person. */
    website: z.string().max(200),
    /** How long the form was open, in milliseconds: a person needs a few seconds. */
    elapsedMs: z.int().nonnegative().max(86_400_000),
})
    .meta({ id: 'InquiryInput' });
/**
 * Whether an inquiry fits the firm's target profile, by the rules of the pack `vorselektion`
 * (ADR-068): `open` while the profile is not set or the inquiry leaves a fact the rules need
 * unstated - unknown is not no. A proposal: no inquiry is turned away by it.
 */
export const PreselectionOutcome = z
    .enum(['fits', 'does_not_fit', 'open'])
    .meta({ id: 'PreselectionOutcome' });
export const preselectionOutcomeLabel = {
    fits: 'Passt',
    does_not_fit: 'Passt nicht',
    open: 'Offen',
};
export const InquiryPreselection = z
    .object({
    outcome: PreselectionOutcome,
    /** The rules that did not let it fit or could not decide, by their titles. */
    reasons: z.array(z.object({ ruleId: z.string(), title: z.string(), outcome: RuleOutcome })),
})
    .meta({ id: 'InquiryPreselection' });
/**
 * The firm's target profile (ADR-068): whom it takes on - kinds of clients, services it offers,
 * regions by postal code prefix (none: everywhere), sizes of organisations. Set by an
 * administrator; without one, every inquiry stays `open`.
 */
export const TargetProfile = z
    .object({
    kinds: z.array(ClientKind).min(1),
    services: z.array(InquiryService).min(1),
    /** Postal code prefixes, e.g. `50` for Cologne; empty: every region. */
    postalPrefixes: z
        .array(z
        .string()
        .trim()
        .regex(/^\d{1,5}$/))
        .max(50),
    employees: z.array(EmployeeBand).min(1),
})
    .meta({ id: 'TargetProfile' });
/** The profile as set, or `null` while none is. */
export const TargetProfileState = z
    .object({ profile: TargetProfile.nullable() })
    .meta({ id: 'TargetProfileState' });
/** Status transitions, the only ones the database allows: open → closed. */
export const InquiryStatus = z.enum(['open', 'closed']).meta({ id: 'InquiryStatus' });
export const Inquiry = z
    .object({
    id: z.uuid(),
    kind: ClientKind,
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    concern: z.string(),
    services: z.array(InquiryService),
    postalCode: z.string().nullable(),
    employees: EmployeeBand.nullable(),
    /** Compared with the target profile when it came in; `null` for inquiries from before. */
    preselection: InquiryPreselection.nullable(),
    privacyNoticeUrl: z.string(),
    receivedAt: z.iso.datetime({ offset: true }),
    status: InquiryStatus,
    closedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Who closed it, by the account's `sub`. */
    closedBy: z.string().nullable(),
})
    .meta({ id: 'Inquiry' });
export const INQUIRY_SORT_FIELDS = ['receivedAt', 'name', 'status'];
export const InquiryQuery = z
    .object({
    ...listQueryShape,
    status: InquiryStatus.optional(),
    kind: ClientKind.optional(),
    preselection: PreselectionOutcome.optional(),
})
    .meta({ id: 'InquiryQuery' });
export const InquiryPage = pageOf(Inquiry, 'InquiryPage');
export const InquiryParams = z.object({ inquiryId: z.uuid() }).meta({ id: 'InquiryParams' });
//# sourceMappingURL=intake.js.map